import MoreVertIcon from "@mui/icons-material/MoreVert";
import { disabledBlockSx } from "../disabledStyles";
import { Box, Button, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { ContentGalleryModal } from "@platformx/authoring-utilities";
import { ShowToastError } from "@platformx/utilities";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSchemaWithData } from "../../../utils/helperFns";
import { getRepeaterValue, setRepeaterValue } from "../../../utils/repeaterPath";
import { DraggableItems } from "../DragableItem/DragableItems";
import HiddenTextBox from "../HiddenTextBox/HiddenTextBox";
import "./QueryOrMultiPicker.css";

// Schema-driven field, folding together AddContent/AddCollection (pick specific
// items) and Query (dynamic tag/search query) into one "Choose Items" button:
//   - picking one or more items in the gallery      -> stored as {type: "items", items, query: {}}
//   - closing the gallery with nothing checked      -> stored as {type: "query", items: [], query}
// The two are mutually exclusive: whichever the editor does last replaces the
// other one's payload. field.variant ("contentType" | "collection") only
// controls the `type` tag written onto each picked item and is otherwise cosmetic.
//
// When rendered inside a (possibly nested) repeater (isRepeater=true), field.name
// is a synthetic RHF path (e.g. `entries[0].foo_entries_123`), not a real state
// key — reads and writes go through field.repeaterPath/childName instead,
// mirroring the same pattern builder.tsx's own handleChange/updateState helpers use.

type QueryOrMultiPickerProps = {
  field: any;
  state: any;
  errors: any;
  setState: any;
  register: any;
  clearErrors: any;
  isEdit: boolean;
  isRepeater?: boolean;
  isDisabled?: boolean;
};

interface PopulatedItem {
  title: string;
  name: string;
  tag_name: string;
  current_page_url: string;
}

type PickerValue = {
  type: "items" | "query" | "";
  items: any[];
  query: any;
};

const EMPTY_VALUE: PickerValue = { type: "", items: [], query: {} };

const readValue = (state: any, field: any, isRepeater: boolean): PickerValue => {
  const raw = isRepeater
    ? getRepeaterValue(state, field?.repeaterPath, field?.childName ?? "")
    : state?.[field?.name];
  return raw && typeof raw === "object" ? { ...EMPTY_VALUE, ...raw } : EMPTY_VALUE;
};

const writeValue = (
  state: any,
  setState: any,
  field: any,
  isRepeater: boolean,
  value: PickerValue,
) => {
  if (isRepeater) {
    setState(setRepeaterValue(state, field?.repeaterPath, field?.childName ?? "", value));
    return;
  }
  setState({
    ...state,
    [field?.name]: value,
  });
};

const QueryOrMultiPicker = (props: QueryOrMultiPickerProps) => {
  const {
    field,
    state,
    errors,
    setState,
    register,
    clearErrors,
    isRepeater = false,
    isDisabled = false,
  } = props;
  const { t } = useTranslation();
  const itemType = field?.variant === "collection" ? "collection" : "content";

  const [isOpen, setIsOpen] = useState(false);
  const [populatedItems, setPopulatedItems] = useState<PopulatedItem[]>([]);
  const [queryOptions, setQueryOptions] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const storedValue = readValue(state, field, isRepeater);
  const hasItems = storedValue.type === "items" && storedValue.items?.length > 0;
  const queryTags = [
    ...(storedValue.query?.selectedCDPFilter?.length > 0
      ? storedValue.query.selectedCDPFilter
      : []),
    ...(storedValue.query?.selectedTags?.length > 0 ? storedValue.query.selectedTags : []),
  ];
  const hasQuery =
    storedValue.type === "query" && (queryTags.length > 0 || !!storedValue.query?.searchTerm);

  const open = Boolean(anchorEl);
  const menuId = open ? "query-or-multi-picker-popover" : undefined;

  const openPicker = () => setIsOpen(true);
  const closePicker = () => setIsOpen(false);

  const filterQueryAdd = (filteredNames: any = [], searchTerm1 = "") => {
    if (filteredNames?.length > 0 && ("" + searchTerm1).length > 0) {
      return `${t("categories")} = ${filteredNames.join(", ")} ${t(
        "and_search_terms",
      )} "${searchTerm1}"`;
    } else if (filteredNames?.length > 0) {
      return `${t("categories")} = ${filteredNames.join(", ")}`;
    } else if (("" + searchTerm1).length > 0) {
      return `${t("search_term")} = "${searchTerm1}"`;
    } else {
      return `${t("all")}`;
    }
  };

  const commonProduct = (arr: string[], searchData: string) => {
    const filteredNames = arr?.map((item) => `"${item}"`);
    const finalData = `${filterQueryAdd(filteredNames, searchData)} ${t("orderByCreateQuery")}`;
    setQueryOptions(finalData);
  };

  //GetSchemaData API - resolves picked item refs into display titles
  const getSchema = async (Items: any) => {
    try {
      const promises = Items?.map((item) =>
        fetchSchemaWithData(item.content_type, item.content_name, item.type),
      );
      const outputs: any = await Promise.allSettled(promises);
      const tempArray: PopulatedItem[] = [];
      outputs?.forEach((output, index) => {
        if (output?.status === "rejected") {
          const fallbackItem = Items?.[index];
          if (fallbackItem?.Title) {
            tempArray.push({
              title: fallbackItem.Title,
              name: fallbackItem.content_name || fallbackItem.Title,
              tag_name: fallbackItem.content_type || "",
              current_page_url: fallbackItem.CurrentPageURL || fallbackItem.content_path || "",
            });
          }
        } else if (output?.status === "fulfilled") {
          const schema = output?.value?.authoring_getSchemaContent || {};
          if (schema && schema.title) {
            tempArray.push({
              title: schema?.title,
              name: schema?.name,
              tag_name: schema?.tag_name,
              current_page_url: schema?.current_page_url,
            });
          } else {
            const fallbackItem = Items?.[index];
            if (fallbackItem?.Title) {
              tempArray.push({
                title: fallbackItem.Title,
                name: fallbackItem.content_name || fallbackItem.Title,
                tag_name: fallbackItem.content_type || "",
                current_page_url: fallbackItem.CurrentPageURL || fallbackItem.content_path || "",
              });
            }
          }
        }
      });
      setPopulatedItems(tempArray);
    } catch (err: any) {
      ShowToastError("Error in fetching row items data");
    }
  };

  // Re-derive the display (item titles or query text) whenever the stored value changes,
  // covering both first load (edit mode) and after a fresh pick/query/clear.
  useEffect(() => {
    if (hasItems) {
      getSchema(storedValue.items);
      setQueryOptions("");
    } else if (hasQuery) {
      commonProduct(queryTags, storedValue.query?.searchTerm);
      setPopulatedItems([]);
    } else {
      setPopulatedItems([]);
      setQueryOptions("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(storedValue)]);

  const handleSelectedContent = (e: any) => {
    clearErrors(field?.name);
    const pickedSlots = e?.slots?.filter((item: any) => item?.is_checked) || [];
    if (pickedSlots.length > 0) {
      const newArray = pickedSlots.map((val: any) => ({
        Title: val.Title,
        content_path: val.path || val.Path || val.content_path || val.CurrentPageURL || "",
        content_name: val.page || val.content_name || val.Title || "",
        CurrentPageURL: val.slug || val.Slug || val.CurrentPageURL || "",
        content_type: val.ContentType || val.content_type,
        is_hidden: false,
        type: itemType,
      }));
      writeValue(state, setState, field, isRepeater, {
        type: "items",
        items: newArray,
        query: {},
      });
    } else {
      writeValue(state, setState, field, isRepeater, {
        type: "query",
        items: [],
        query: e?.selectedFilter || {},
      });
    }
    closePicker();
  };

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => setAnchorEl(null);

  const onEditQuery = () => {
    openPicker();
    handleCloseMenu();
  };

  const onDeleteQuery = () => {
    handleCloseMenu();
    writeValue(state, setState, field, isRepeater, EMPTY_VALUE);
  };

  // Adapter so the shared DraggableItems component (which reads/writes `state[name]`
  // directly as a flat array) can drive the nested {type, items, query} value here.
  const draggableAdapterState = { [field?.name]: hasItems ? storedValue.items : [] };
  const draggableAdapterSetState = (adapterState: any) => {
    const updatedItems = adapterState?.[field?.name] ?? [];
    writeValue(state, setState, field, isRepeater, {
      type: "items",
      items: updatedItems,
      query: {},
    });
  };

  const isEmptyValue = storedValue.type === "" || (!hasItems && !hasQuery);

  return (
    <Box id={`${field?.name}_id`} sx={disabledBlockSx(isDisabled)}>
      <Button variant='primaryButton' onClick={openPicker}>
        {field?.placeholder || "Choose Items"}
      </Button>

      {hasItems && (
        <Box
          sx={{
            ".draggable-category": {
              paddingRight: "0",
            },
          }}>
          <DraggableItems
            state={draggableAdapterState}
            setState={draggableAdapterSetState}
            name={field?.name}
            populatedItems={populatedItems}
          />
        </Box>
      )}

      {hasQuery && (
        <Box className='queryBox'>
          <Box component={"span"} className='queryBox_content'>
            {queryOptions}
          </Box>
          <IconButton
            onClick={handleClickListItem}
            className='queryBoxOption'
            data-testid='selectoption'>
            <MoreVertIcon />
          </IconButton>
        </Box>
      )}

      {isOpen ? (
        <ContentGalleryModal
          isDialogOpen={isOpen}
          onClose={closePicker}
          handleSelectedContent={handleSelectedContent}
          selectedItem={hasItems ? storedValue.items : []}
          multi_select={field?.multiple}
          single_select={!field?.multiple}
          allowEmptySelection={true}
          name={field?.name}
          allowed_content={field?.allowed_content}
          allowed_collection={field?.allowed_collection}
          // Only a query-type selection has a tag/search filter worth restoring - an items-type
          // pick has none (its query is always saved as {}), so the picker opens tag-fresh.
          selected_filter={storedValue.type === "query" ? storedValue.query : undefined}
        />
      ) : null}

      <HiddenTextBox
        error={errors?.[field?.name]}
        value={hasItems ? storedValue.items.length : queryOptions}
        register={register}
        name={field?.name}
        required={
          isEmptyValue ? field?.validations?.find((v: any) => v.type === "required")?.message : ""
        }
      />

      <Menu
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: -5,
        }}>
        <MenuItem sx={{ p: 1 }}>
          <Typography onClick={onEditQuery} sx={{ padding: "3px 10px 3px 10px" }}>
            {t("edit")}
          </Typography>
        </MenuItem>
        <MenuItem sx={{ p: 1 }}>
          <Typography onClick={onDeleteQuery} sx={{ padding: "3px 10px 3px 10px" }}>
            {t("delete")}
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default memo(QueryOrMultiPicker);
